const shopTheLookTrackingCategory = 'wear it with'
const viewProductAction = 'view product'

import { FormProvider } from '@ariakit/react'
import { useSearchParams } from 'next/navigation'
import { stringifyUrl } from 'query-string'

import { Skeleton, Spacer } from '@syconium/little-miss-figgy'
import {
  TypeStyle,
  TypeStyleHeadlineMicro,
} from '@syconium/little-miss-figgy/dist/components/TypeStyle'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import { QuickBuyDialog } from '../../../../../app/_components/dialogs/QuickbuyDialog.client'
import { VariantSelectionColorRadioGroup } from '../../../../../app/_components/forms/VariantSelectionColorField'
import { VariantSelectionFitRadioGroup } from '../../../../../app/_components/forms/VariantSelectionFitField'
import {
  VariantSelectionForm,
  VariantSelectionFormFields,
  useVariantSelectionForm,
} from '../../../../../app/_components/forms/VariantSelectionForm'
import { VariantSelectionSizeRadioGroup } from '../../../../../app/_components/forms/VariantSelectionSizeField'
import { VariantSelectionFormPurchasingSubmit } from '../../../../../app/_components/forms/VariantSelectionSubmit'
import { NextLink } from '../../../../../app/_components/navigation/NextLink'
import {
  PricingRange,
  derivePricingForVariant,
} from '../../../../../app/_components/pricing/PricingRange.client'
import { useNextQuery } from '../../../../../app/_hooks/useNextQuery'
import { useTrackProductInteraction } from '../../../../../app/_providers/TrackingProvider.client'
import { trackEvent } from '../../../../../lib/analytics/analytics'

import {
  ColorName,
  DesktopButtonCtaWrapper,
  DesktopContents,
  PriceAndColor,
  QuickBuyButtonWrapper,
  Tile,
  TileTextLink,
  TileTextSkeleton,
  VariantAttributeSelectorsWrapper,
} from './styles'
import { ShopTheLookTileImage, ShopTheLookTileSkeletonImage } from './styles'

export interface ShopTheLookTileProps {
  productGroupHandle: string
  colorKey: string
  collectionHandle: string | undefined | null
}

const ShopTheLookTileImpl = ({
  productGroupHandle,
  colorKey,
  collectionHandle,
}: ShopTheLookTileProps) => {
  const { trackProductInteraction } = useTrackProductInteraction()

  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined

  const { formStore, queryVariables } = useVariantSelectionForm({
    defaultValues: {
      initialColor: colorKey,
      initialFit: undefined,
      initialSize: undefined,
    },
  })

  const {
    data: currentData,
    previousData,
    status,
  } = useNextQuery(
    gql(`
      query GetShopTheLookProductInfo(
          $collectionHandle: String, 
          $catalogProductHandle: String!, 
          $colorFieldValue: String!, 
          $fitFieldValue: String!, 
          $sizeFieldValue: String!
      ) {
        productGroup(handle: $catalogProductHandle) {
          sizeChartCG {
            ...SizeChartFragment
          }
        }
        productCatalogStandardProduct(handle: $catalogProductHandle) {
          ...VariantSelectionFormProductFields
          variantGroup(color: $colorFieldValue) {
            ...VariantSelectionFormGroupFields
            urlPath
            defaultVariant {
              shopifyProductId
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
              }
            }
            images(first: 1, fit: $fitFieldValue) {
              url
            }
            priceRange {
              currencyCode
              min {
                amount
              }
              max {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              min {
                amount
              }
              max {
                amount
              }
            }
          }
        }
      }
    `),
    {
      variables: {
        ...queryVariables,
        catalogProductHandle: productGroupHandle,
        collectionHandle,
      },
    }
  )

  const data = currentData ?? previousData

  if (!data) {
    if (status === 'pending') {
      return <ShopTheLookSkeletonTile />
    }
    // TODO: Handle notfound, error states
    return null
  }

  const product = data.productCatalogStandardProduct
  const variantGroup = data.productCatalogStandardProduct.variantGroup
  const selectedVariant = data.productCatalogStandardProduct.variantGroup.variant
  const shopifyProductId =
    data.productCatalogStandardProduct.variantGroup.variant?.shopifyProductId ??
    data.productCatalogStandardProduct.variantGroup.defaultVariant.shopifyProductId

  const pdpHref = stringifyUrl({
    url: selectedVariant?.urlPath ?? variantGroup.urlPath,
    query: { plain: plainQueryParamValue },
  })
  const imageSource = variantGroup.images?.[0]?.url
  const pricingRange = selectedVariant
    ? derivePricingForVariant(selectedVariant)
    : derivePricingForVariant(variantGroup.defaultVariant)

  const onProductView = () => {
    trackProductInteraction({
      handle: product.handle,
      color: variantGroup.color?.id ?? '',
      shopifyId: shopifyProductId,
      id: product.variantGroup.id,
    })
  }

  return (
    <Tile>
      {imageSource ? (
        <NextLink
          href={pdpHref}
          onClick={onProductView}
          {...trackEvent({ category: shopTheLookTrackingCategory, action: viewProductAction })}
        >
          <ShopTheLookTileImage
            className='flex'
            alt={product.variantGroup.title}
            aspectRatios={{
              sm: 1,
              md: 1,
              lg: 0.8,
            }}
            src={imageSource}
            loading={'lazy'}
            widths={{
              unit: 'px',
              sm: 85,
              md: 85,
              lg: 250, // We render at 200 width, but our height is 250 and these files are 1:1. So being safe here.
            }}
          />
        </NextLink>
      ) : null}

      <TileTextLink
        href={pdpHref}
        onClick={onProductView}
        {...trackEvent({ category: shopTheLookTrackingCategory, action: viewProductAction })}
      >
        <TypeStyle.HeadlineExtraExtraSmall>
          {product.variantGroup.title}
        </TypeStyle.HeadlineExtraExtraSmall>

        <PriceAndColor>
          {pricingRange ? (
            <TypeStyleHeadlineMicro>
              <span>
                <PricingRange {...pricingRange} />
              </span>
            </TypeStyleHeadlineMicro>
          ) : null}

          {variantGroup.color?.displayValue && (
            <ColorName color='secondary'>
              <span>{variantGroup.color.displayValue}</span>
            </ColorName>
          )}
        </PriceAndColor>
      </TileTextLink>

      <QuickBuyButtonWrapper>
        <QuickBuyDialog
          position='inline'
          productGroupHandle={product.handle}
          collectionHandle={collectionHandle}
          initialColor={product.variantGroup.color?.id ?? colorKey}
          initialFit={selectedVariant?.fit?.id}
          initialSize={selectedVariant?.size?.id}
        />
      </QuickBuyButtonWrapper>

      <VariantSelectionForm
        store={formStore}
        product={product}
        selectedVariant={selectedVariant}
        variantGroup={variantGroup}
        trackingCategory={shopTheLookTrackingCategory}
        loading={status === 'pending'}
      >
        <DesktopContents>
          <VariantAttributeSelectorsWrapper>
            <VariantSelectionFormFields>
              <VariantSelectionColorRadioGroup />
              <VariantSelectionSizeRadioGroup sizeChart={data.productGroup?.sizeChartCG} />
              <VariantSelectionFitRadioGroup />
            </VariantSelectionFormFields>
          </VariantAttributeSelectorsWrapper>

          <DesktopButtonCtaWrapper>
            <VariantSelectionFormPurchasingSubmit isPrimaryCtaForPageView={false} />
          </DesktopButtonCtaWrapper>
        </DesktopContents>
      </VariantSelectionForm>
    </Tile>
  )
}

export function ShopTheLookTile(props: ShopTheLookTileProps) {
  return (
    <FormProvider>
      <ShopTheLookTileImpl {...props} />
    </FormProvider>
  )
}

const ShopTheLookSkeletonTile = () => {
  return (
    <Tile>
      <ShopTheLookTileSkeletonImage />

      <TileTextSkeleton>
        <TypeStyle.HeadlineExtraExtraSmall>
          <Skeleton height={'20px'} width={'200px'}></Skeleton>
        </TypeStyle.HeadlineExtraExtraSmall>

        <PriceAndColor>
          <Skeleton height={'20px'} width={'100px'}></Skeleton>
        </PriceAndColor>
      </TileTextSkeleton>

      <QuickBuyButtonWrapper>
        <Skeleton height={'32px'} width={'32px'}></Skeleton>
      </QuickBuyButtonWrapper>

      <DesktopContents>
        <VariantAttributeSelectorsWrapper>
          <Skeleton height={'30px'} width={'150px'}></Skeleton>
          <Spacer height={8} width={1}></Spacer>
          <Skeleton height={'30px'} width={'400px'}></Skeleton>
          <Spacer height={24} width={1}></Spacer>
          <Skeleton height={'30px'} width={'150px'}></Skeleton>
          <Spacer height={8} width={1}></Spacer>
          <Skeleton height={'30px'} width={'400px'}></Skeleton>
          <Spacer height={24} width={1}></Spacer>
          <Skeleton height={'30px'} width={'150px'}></Skeleton>
          <Spacer height={8} width={1}></Spacer>
          <Skeleton height={'30px'} width={'400px'}></Skeleton>
          <Spacer height={24} width={1}></Spacer>
        </VariantAttributeSelectorsWrapper>

        <DesktopButtonCtaWrapper>
          <Skeleton height={'50px'} width={'100%'}></Skeleton>
          <Spacer height={24} width={1}></Spacer>
        </DesktopButtonCtaWrapper>
      </DesktopContents>
    </Tile>
  )
}
